/**
 * 系统字典 code → label 的查找函数（一次建 Map，稳定引用）。
 *
 * 语义：传 code 且字典里有 → label；有 code 但字典没有（或还没加载）→ 回落 code 本身
 * （界面上至少看得到原始值，不会出现空白）；没有 code → null，由调用方决定占位符。
 *
 * 为什么单独一个模块而不放进 useLookup.ts：全仓 100+ 个测试用 `vi.mock('@/hooks/useLookup')`
 * 只桩了 `useSystemLookup`，把新 hook 塞进同一模块会让这些桩全部缺一个导出。独立模块
 * 从被桩的 `useSystemLookup` 取数，既有测试的桩照常生效。
 *
 * Task Costing 看板 / Expense 表 / TaskRail 之前各自手写了一份同样的 Map，收拢到这里。
 *
 * 取数带 includeDisabled：这是「显示」用的标签表，历史数据可能引用已停用的 code（如
 * expense_category_type 2026-04-29 停用的 materials），停用只意味着以后不再选，照样要显示正式标签。
 *
 * 标签按当前界面语言取（2026-09-26）：以前拿的是字典的英文 `label`，中文界面显示
 * "Materials"。字典本身带 `labelI18n`（`{en,zh,pt,es}`，要 includeExtra 才下发），
 * 按 `pickLookupLabel(labelI18n, locale, label)` 取：英文界面 / 缺译一律回落规范 label（不取 labelI18n.en，
 * 见该函数注释），不会串到别的语言。
 *
 * ⚠️ 仅限 lookup-table 字典（后端 lookup.* 表，api db/seeds/lookup/*.json 那 39 个）。includeExtra 走
 * GET /system/lookup-table/tables/{code}?include_extra=true，不是 lookup-table 的字典（如
 * task_resolution_reason_type）在这个端点 404 NOT_FOUND_LOOKUPTABLE，标签会全部回落成原始 code ——
 * 那类字典用它自己的 hook（useTaskResolutionReasons）。
 */
import { useMemo } from 'react';
import { useSystemLookup } from '@/hooks/useLookup';
import { useLocale } from '@/hooks/useLocale';
import { pickLookupLabel } from '@/utils/localizeLookupOptions';

export const useSystemLookupLabel = (dictionaryName: string): ((code?: string | null) => string | null) => {
  const { data } = useSystemLookup(dictionaryName, { includeExtra: true, includeDisabled: true });
  const locale = useLocale();
  return useMemo(() => {
    const m = new Map<string, string>();
    for (const o of (data ?? []) as Array<{ code: string; label?: string; labelI18n?: Record<string, string> | null }>) {
      m.set(o.code, pickLookupLabel(o.labelI18n, locale, o.label ?? o.code));
    }
    return (code?: string | null) => (code ? (m.get(code) ?? code) : null);
  }, [data, locale]);
};
